<script setup lang="ts">
/**
 * ModuleLinksSection —— 首页模块链接卡（新增，type=module-links）。
 *
 * 渲染一个标题（如「申报书」、「佐证材料」、「成果总结报告」）和「更多+」入口，
 * 内部以 2 列网格渲染对应模块的子导航项，支持点击直接跳转到对应的模块内容。
 * 完备对接 Aggregate 中的 navItems、sections 与路由，数据完全驱动。
 */
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import SectionCard from './SectionCard.vue';
import GridItem from './GridItem.vue';
import { useShowcase } from '@/store/use-showcase';
import { parseTarget } from '@/composables/nav-target';
import { moduleRouteLocation } from '@/composables/module-target';
import { detailRouteLocation } from '@/composables/detail-path';
import type { NavItemView, PublicSectionView } from '@/api/types';

const props = defineProps<{
  readonly id?: string;
  readonly type: string;
  readonly section: PublicSectionView;
  readonly showDraftBadge?: boolean;
  readonly previewToken?: string | null;
}>();

const store = useShowcase();

/** props.section.summary 保存了目标路由模块名称，例如 'application-form' */
const routeName = computed(() => props.section.summary || '');

/** 查找对应模块的一级导航节点，以获取其二级子节点 */
const parentItem = computed<NavItemView | null>(() => {
  if (!routeName.value || !store.state.aggregate) {
    return null;
  }
  const target = `module:${routeName.value}`;
  return store.state.aggregate.navItems.find(
    (item) => item.targetAnchor === target && item.parentId === null,
  ) ?? null;
});

/** 排序好的子导航项 */
const children = computed<readonly NavItemView[]>(() => {
  return parentItem.value?.children ?? [];
});

function getRouteLocation(targetAnchor: string) {
  const target = parseTarget(targetAnchor);
  if (target.kind === 'module') {
    return moduleRouteLocation(target.routeName, target.moduleId, {
      anchor: target.anchor,
      previewToken: props.previewToken,
    });
  }
  if (target.kind === 'detail') {
    const section = store.state.aggregate?.sections.find((s) => s.id === target.sectionId)
      ?? {
        id: target.sectionId,
        type: '',
        title: '',
        summary: '',
        bodyHtml: '',
        materials: [],
        images: [],
        sidebar: [],
        sort: 0,
        publishStatus: 'Draft',
        createdAt: '',
        updatedAt: '',
        cover: null,
        videoUrl: null,
        posterUrl: null,
      };
    return detailRouteLocation(section, {
      anchor: target.anchor,
      previewToken: props.previewToken,
    });
  }
  if (target.kind === 'home-anchor') {
    return { path: '/', hash: target.hash };
  }
  return '/';
}

const mainRouteLocation = computed(() => {
  if (!routeName.value) {
    return '/';
  }
  return moduleRouteLocation(routeName.value as any, undefined, {
    previewToken: props.previewToken,
  });
});
</script>

<template>
  <SectionCard
    :id="props.id"
    :type="props.type"
    :title="props.section.title"
    :show-draft-badge="props.showDraftBadge"
  >
    <template #more>
      <RouterLink
        :to="mainRouteLocation"
        :class="$style.more"
        data-testid="module-links-more"
      >
        更多+
      </RouterLink>
    </template>

    <div :class="$style.grid" data-shell="scu-grid">
      <RouterLink
        v-for="child in children"
        :key="child.id"
        :to="getRouteLocation(child.targetAnchor)"
        :class="$style.link"
        :data-testid="`module-links-item-${child.id}`"
      >
        <GridItem :title="child.displayName" />
      </RouterLink>
    </div>
  </SectionCard>
</template>

<style module>
.grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--template-grid-gap);
  margin-top: var(--space-2);
}

.link {
  text-decoration: none;
  color: inherit;
  display: block;
}

.link [data-shell="scu-grid-item"] h4 {
  font-weight: normal;
  color: var(--color-text-primary);
}

.more {
  flex: 0 0 auto;
  align-self: flex-end;
  margin-bottom: var(--template-title-icon-gap);
  color: var(--color-brand-accent);
  font-size: var(--font-size-sm);
  text-decoration: none;
}

.more:hover {
  color: var(--color-brand-primary);
}

@media (max-width: 719px) {
  .grid {
    grid-template-columns: 1fr;
  }
}
</style>
